import { useState, useEffect, useRef } from 'react';
import { capturePostHogEvent, getFeatureFlag, POSTHOG_KEY, pushEvent } from '../utils/analytics';
import {
  EXPERIMENT_PREVIEW_OVERRIDE_EVENT,
  getExperimentPreviewOverride,
} from '../utils/experimentPreviewOverrides';

const POSTHOG_READY_TIMEOUT_MS = 5000;
const FEATURE_FLAGS_READY_TIMEOUT_MS = 3000;

const getPostHogFeatureFlagsClient = () => {
  if (typeof window === 'undefined' || !window.posthog) {
    return null;
  }

  return window.posthog.featureFlags || window.posthog;
};

const getPostHogFeatureFlagsListener = () => {
  if (typeof window === 'undefined' || !window.posthog) {
    return null;
  }

  if (typeof window.posthog.onFeatureFlags === 'function') {
    return window.posthog.onFeatureFlags.bind(window.posthog);
  }

  const featureFlagsClient = getPostHogFeatureFlagsClient();
  if (featureFlagsClient && typeof featureFlagsClient.onFeatureFlags === 'function') {
    return featureFlagsClient.onFeatureFlags.bind(featureFlagsClient);
  }

  return null;
};

const hasLoadedPostHogFeatureFlags = () => {
  const featureFlagsClient = getPostHogFeatureFlagsClient();

  if (!featureFlagsClient) {
    return false;
  }

  if (featureFlagsClient._hasLoadedFlags === true) {
    return true;
  }

  if (typeof featureFlagsClient.getFlags === 'function') {
    const flags = featureFlagsClient.getFlags();
    if (Array.isArray(flags) && flags.length > 0) {
      return true;
    }
  }

  if (typeof featureFlagsClient.getFlagVariants === 'function') {
    const flagVariants = featureFlagsClient.getFlagVariants();
    if (flagVariants && typeof flagVariants === 'object' && Object.keys(flagVariants).length > 0) {
      return true;
    }
  }

  return false;
};

const waitForPostHog = (experimentName, timeoutMs = POSTHOG_READY_TIMEOUT_MS) => new Promise((resolve) => {
  if (typeof window === 'undefined') {
    resolve(false);
    return;
  }

  let settled = false;
  let timeoutId;
  let pollId;

  const settle = (isReady) => {
    if (settled) {
      return;
    }

    settled = true;
    clearTimeout(timeoutId);
    clearTimeout(pollId);
    resolve(isReady);
  };

  const checkPostHog = () => {
    if (window.posthog && typeof window.posthog.getFeatureFlag === 'function') {
      console.debug(`🎯 [A/B Test] PostHog ready for ${experimentName}`);
      settle(true);
      return;
    }

    console.debug(`⏳ [A/B Test] Waiting for PostHog to load for ${experimentName}...`);
    pollId = setTimeout(checkPostHog, 100);
  };

  timeoutId = setTimeout(() => settle(false), timeoutMs);
  checkPostHog();
});

const waitForPostHogFeatureFlags = (
  featureFlagKey,
  timeoutMs = FEATURE_FLAGS_READY_TIMEOUT_MS,
) => new Promise((resolve) => {
  if (typeof window === 'undefined' || !window.posthog) {
    resolve(false);
    return;
  }

  if (hasLoadedPostHogFeatureFlags()) {
    console.debug(`🎯 [A/B Test] Feature flags already loaded for ${featureFlagKey}`);
    resolve(true);
    return;
  }

  const onFeatureFlags = getPostHogFeatureFlagsListener();
  if (!onFeatureFlags) {
    resolve(false);
    return;
  }

  let settled = false;
  let unsubscribe;
  let timeoutId;

  const settle = (didLoadFlags) => {
    if (settled) {
      return;
    }

    settled = true;
    clearTimeout(timeoutId);

    if (typeof unsubscribe === 'function') {
      unsubscribe();
    }

    resolve(didLoadFlags);
  };

  timeoutId = setTimeout(() => settle(false), timeoutMs);

  try {
    unsubscribe = onFeatureFlags(() => {
      console.debug(`🎯 [A/B Test] Feature flags loaded for ${featureFlagKey}`);
      settle(true);
    });

    if (settled && typeof unsubscribe === 'function') {
      unsubscribe();
    }
  } catch (error) {
    console.warn(`PostHog feature flags listener failed for ${featureFlagKey}:`, error);
    settle(false);
  }
});

const isActiveFeatureFlagValue = (value) => value !== false && value !== null && value !== undefined;

const getPostHogFlagCaptureProperties = (featureFlagKey, exposureFlagValue) => {
  if (typeof window === 'undefined' || !window.posthog) {
    return {};
  }

  const posthogProps = window.posthog?.persistence?.props || {};
  const flagDetails = posthogProps.$feature_flag_details?.[featureFlagKey] || {};
  const flagMetadata = flagDetails.metadata || {};
  const flagReason = flagDetails.reason || {};
  const enabledFeatureFlags = {
    ...(
      posthogProps.$enabled_feature_flags &&
      typeof posthogProps.$enabled_feature_flags === 'object' &&
      !Array.isArray(posthogProps.$enabled_feature_flags)
        ? posthogProps.$enabled_feature_flags
        : {}
    ),
  };
  const activeFeatureFlags = Array.isArray(posthogProps.$active_feature_flags)
    ? [...posthogProps.$active_feature_flags]
    : [];

  if (isActiveFeatureFlagValue(exposureFlagValue)) {
    enabledFeatureFlags[featureFlagKey] = exposureFlagValue;
    if (!activeFeatureFlags.includes(featureFlagKey)) {
      activeFeatureFlags.push(featureFlagKey);
    }
  }

  return {
    [`$feature/${featureFlagKey}`]: exposureFlagValue,
    ...(activeFeatureFlags.length > 0 && { $active_feature_flags: activeFeatureFlags }),
    ...(Object.keys(enabledFeatureFlags).length > 0 && { $enabled_feature_flags: enabledFeatureFlags }),
    ...(flagMetadata.id && { $feature_flag_id: flagMetadata.id }),
    ...(flagMetadata.version && { $feature_flag_version: flagMetadata.version }),
    ...(flagReason.description && { $feature_flag_reason: flagReason.description }),
    ...(posthogProps.$feature_flag_request_id && { $feature_flag_request_id: posthogProps.$feature_flag_request_id }),
    ...(posthogProps.$feature_flag_payloads && { $feature_flag_payloads: posthogProps.$feature_flag_payloads }),
  };
};

const trackExperimentExposure = ({
  experimentName,
  featureFlagKey,
  selectedVariant,
  exposureFlagValue,
  exposureIsActive,
  eventTrackingData,
}) => {
  const posthogFlagProperties = getPostHogFlagCaptureProperties(featureFlagKey, exposureFlagValue);

  capturePostHogEvent('$feature_flag_called', {
    $feature_flag: featureFlagKey,
    $feature_flag_response: exposureFlagValue,
    experiment_name: experimentName,
    variant: selectedVariant,
    is_experiment_active: exposureIsActive,
    ...posthogFlagProperties,
    ...eventTrackingData
  });

  // Also track our custom experiment exposure event
  capturePostHogEvent('experiment_exposure', {
    experiment_name: experimentName,
    variant: selectedVariant,
    feature_flag: featureFlagKey,
    feature_flag_value: exposureFlagValue,
    is_experiment_active: exposureIsActive,
    ...posthogFlagProperties,
    ...eventTrackingData
  });
};

/**
 * useABTest - Generic A/B Testing Hook
 * 
 * This hook can be used anywhere in the app to run A/B tests using PostHog feature flags.
 * It automatically tracks experiment exposure and provides the variant information.
 * 
 * @param {string} experimentName - Name of the experiment (for tracking)
 * @param {string} featureFlagKey - PostHog feature flag key
 * @param {*} defaultVariant - Default variant if flag fails (usually 'control')
 * @param {Object} trackingData - Additional data to track with the experiment
 * @returns {Object} { variant, isLoading, isExperimentActive }
 */
const useABTest = (experimentName, featureFlagKey, defaultVariant = 'control', trackingData = {}) => {
  const [variant, setVariant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExperimentActive, setIsExperimentActive] = useState(false);
  const [isPreviewOverride, setIsPreviewOverride] = useState(false);
  const [, setPreviewOverrideRevision] = useState(0);
  const trackingDataRef = useRef(trackingData);
  trackingDataRef.current = trackingData;
  const callerHasForcedVariant = Boolean(
    trackingData?.skipFeatureFlagEvaluation &&
    trackingData?.forcedVariant !== null &&
    trackingData?.forcedVariant !== undefined,
  );
  const settingsPreviewOverride = callerHasForcedVariant
    ? null
    : getExperimentPreviewOverride(featureFlagKey, trackingData?.validVariants);
  const trackingConfigKey = JSON.stringify({
    skipFeatureFlagEvaluation: trackingData?.skipFeatureFlagEvaluation,
    forcedVariant: trackingData?.forcedVariant,
    forcedExposureValue: trackingData?.forcedExposureValue,
    trackForcedExposure: trackingData?.trackForcedExposure,
    normalizeControlExposure: trackingData?.normalizeControlExposure,
    validVariants: trackingData?.validVariants,
    settingsPreviewOverride,
  });

  useEffect(() => {
    const browserWindow = window;

    const handlePreviewOverrideChange = (event) => {
      const changedFeatureFlag = event?.detail?.featureFlagKey;
      if (!changedFeatureFlag || changedFeatureFlag === featureFlagKey) {
        setPreviewOverrideRevision((revision) => revision + 1);
      }
    };

    browserWindow.addEventListener(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, handlePreviewOverrideChange);
    return () => {
      browserWindow.removeEventListener(
        EXPERIMENT_PREVIEW_OVERRIDE_EVENT,
        handlePreviewOverrideChange,
      );
    };
  }, [featureFlagKey]);

  useEffect(() => {
    let cancelled = false;

    const runExperiment = async () => {
      setIsLoading(true);
      const {
        skipFeatureFlagEvaluation,
        forcedVariant,
        forcedExposureValue,
        trackForcedExposure,
        normalizeControlExposure,
        validVariants,
        ...eventTrackingData
      } = trackingDataRef.current || {};

      const hasCallerForcedVariant = Boolean(
        skipFeatureFlagEvaluation && forcedVariant !== null && forcedVariant !== undefined,
      );
      const appliedPreviewOverride = hasCallerForcedVariant ? null : settingsPreviewOverride;
      const appliedForcedVariant = hasCallerForcedVariant ? forcedVariant : appliedPreviewOverride;

      if (appliedForcedVariant !== null && appliedForcedVariant !== undefined) {
        setVariant(appliedForcedVariant);
        setIsExperimentActive(true);
        setIsPreviewOverride(Boolean(appliedPreviewOverride));
        if (hasCallerForcedVariant && trackForcedExposure && POSTHOG_KEY) {
          void (async () => {
            try {
              await waitForPostHog(experimentName);
              await waitForPostHogFeatureFlags(featureFlagKey);
              if (cancelled) return;
              const exposureFlagValue = forcedExposureValue ?? appliedForcedVariant;
              trackExperimentExposure({
                experimentName,
                featureFlagKey,
                selectedVariant: appliedForcedVariant,
                exposureFlagValue,
                exposureIsActive: isActiveFeatureFlagValue(exposureFlagValue),
                eventTrackingData,
              });
            } catch (error) {
              console.warn(`PostHog forced exposure tracking failed for ${featureFlagKey}:`, error);
            }
          })();
        }
        setIsLoading(false);
        return;
      }

      if (!POSTHOG_KEY) {
        setVariant(defaultVariant);
        setIsExperimentActive(false);
        setIsPreviewOverride(false);
        setIsLoading(false);
        return;
      }

      try {
        await waitForPostHog(experimentName);
        await waitForPostHogFeatureFlags(featureFlagKey);
        if (cancelled) return;

        // Get the feature flag value from PostHog
        const flagValue = await getFeatureFlag(featureFlagKey, false);
        if (cancelled) return;
        
        // 🔍 DEBUG: Show exactly what PostHog returned
        console.debug(`🔍 [A/B Test Debug] ${experimentName} PostHog response:`, {
          flagKey: featureFlagKey,
          rawFlagValue: flagValue,
          flagType: typeof flagValue,
          windowPosthogExists: !!window.posthog,
          postHogHasGetFeatureFlag: !!(window.posthog && window.posthog.getFeatureFlag)
        });
        
        // Determine the variant based on flag value
        let selectedVariant;
        if (typeof flagValue === 'boolean') {
          // Simple boolean flag: true = test, false = control (PostHog experiments)
          selectedVariant = flagValue ? 'test' : 'control';
        } else if (typeof flagValue === 'string') {
          // String flag: use the string value as variant name (PostHog experiments return custom strings)
          selectedVariant = flagValue;
        } else if (flagValue === null || flagValue === undefined) {
          // No flag value or experiment not running
          selectedVariant = defaultVariant;
        } else {
          // Fallback to default
          selectedVariant = defaultVariant;
        }

        let normalizedInvalidVariant = false;
        if (
          Array.isArray(validVariants) &&
          typeof selectedVariant === 'string' &&
          !validVariants.includes(selectedVariant)
        ) {
          selectedVariant = defaultVariant;
          normalizedInvalidVariant = true;
        }

        const shouldNormalizeToControl =
          normalizeControlExposure === true &&
          selectedVariant === defaultVariant &&
          (
            normalizedInvalidVariant ||
            flagValue === false ||
            flagValue === null ||
            flagValue === undefined
          );
        const exposureFlagValue = shouldNormalizeToControl ? selectedVariant : flagValue;
        const exposureIsActive = isActiveFeatureFlagValue(exposureFlagValue);

        setVariant(selectedVariant);
        setIsExperimentActive(exposureIsActive);
        setIsPreviewOverride(false);

        trackExperimentExposure({
          experimentName,
          featureFlagKey,
          selectedVariant,
          exposureFlagValue,
          exposureIsActive,
          eventTrackingData,
        });

        console.debug(`🧪 [A/B Test] ${experimentName}:`, {
          featureFlag: featureFlagKey,
          flagValue,
          selectedVariant,
          isActive: exposureIsActive
        });

        // Debug: Log what we're sending to PostHog
        console.debug(`📊 [PostHog Debug] Sending experiment exposure events:`, {
          feature_flag_called: {
            event: '$feature_flag_called',
            $feature_flag: featureFlagKey,
            $feature_flag_response: exposureFlagValue
          },
          experiment_exposure: {
            event: 'experiment_exposure',
            experiment_name: experimentName,
            variant: selectedVariant
          }
        });

      } catch (error) {
        if (cancelled) return;
        console.error(`❌ [A/B Test] Error in ${experimentName}:`, error);
        setVariant(defaultVariant);
        setIsExperimentActive(false);
        setIsPreviewOverride(false);
        
        // Track the error
        pushEvent({
          event: 'experiment_error',
          experiment_name: experimentName,
          variant: defaultVariant,
          feature_flag: featureFlagKey,
          error: 'feature_flag_error',
          error_message: error.message,
          ...eventTrackingData
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    runExperiment();
    return () => {
      cancelled = true;
    };
  }, [experimentName, featureFlagKey, defaultVariant, settingsPreviewOverride, trackingConfigKey]);

  return {
    variant,
    isLoading,
    isExperimentActive,
    isPreviewOverride,
    // Helper functions
    isControl: variant === 'control' || variant === false || variant === null,
    isTest: variant && variant !== 'control' && variant !== false && variant !== null,
    isVariant: variant && variant !== 'control' && variant !== defaultVariant, // Legacy support
    // For boolean tests (PostHog experiments)
    isA: variant === 'control' || variant === false || variant === null,
    isB: variant && variant !== 'control' && variant !== false && variant !== null
  };
};

export default useABTest; 
